// Sugalarm — /api/backgrounds wrappers (cycle 84).
//
// Theme background images. Upload is multipart/form-data (bypasses the
// JSON `request` helper — see photo.ts for the same pattern). The raw
// bytes are served behind auth, so a plain <img src> / CSS url() can't
// load them (no Authorization header); `fetchImageObjectUrl` fetches
// the blob with the bearer and hands back an object URL the caller is
// responsible for revoking.

import { authedFetch, request } from "../lib/apiClient";
import type {
  ApiError,
  ApiErrorKind,
  ApiResult,
  Background,
} from "../lib/types";

interface BackgroundListOut {
  backgrounds: Background[];
}

function classify(status: number): ApiErrorKind {
  if (status === 401) return "auth";
  if (status === 403) return "forbidden";
  if (status === 404) return "not_found";
  if (status === 409) return "validation";
  if (status === 413) return "validation";
  if (status === 415 || status === 422) return "validation";
  if (status >= 500) return "server";
  return "unknown";
}

async function readError(res: Response): Promise<ApiError> {
  let detail: unknown;
  let message = res.statusText || `HTTP ${res.status}`;
  try {
    const data = await res.json();
    detail = data;
    const d = (data as { detail?: unknown })?.detail;
    if (typeof d === "string") message = d;
  } catch {
    /* not JSON */
  }
  return { kind: classify(res.status), status: res.status, message, details: detail };
}

export async function list(): Promise<ApiResult<Background[]>> {
  const r = await request<BackgroundListOut>("/api/backgrounds");
  if (!r.ok) return r;
  return { ok: true, data: r.data?.backgrounds ?? [] };
}

/**
 * Cycle 87: upload a background. The caller always passes the
 * client-cropped, canvas-re-encoded JPEG `Blob` (never original file
 * bytes) plus its pixel dimensions — so the server only ever stores
 * JPEG, regardless of the source format (HEIC, PNG, …).
 */
export async function upload(
  blob: Blob,
  width: number,
  height: number,
  filename?: string,
): Promise<ApiResult<Background>> {
  const form = new FormData();
  form.append("image", blob, filename || "background.jpg");
  form.append("width", String(width));
  form.append("height", String(height));
  let res: Response;
  try {
    res = await authedFetch("/api/backgrounds", { method: "POST", body: form });
  } catch (e) {
    return {
      ok: false,
      error: {
        kind: "network",
        status: 0,
        message: e instanceof Error ? e.message : "network error",
      },
    };
  }
  if (!res.ok) return { ok: false, error: await readError(res) };
  try {
    return { ok: true, data: (await res.json()) as Background };
  } catch {
    return {
      ok: false,
      error: { kind: "unknown", status: res.status, message: "bad response" },
    };
  }
}

export async function remove(id: string): Promise<ApiResult<void>> {
  return request<void>(`/api/backgrounds/${id}`, { method: "DELETE" });
}

/** Fetch the raw image with auth and return an object URL. Returns null
 *  on any failure. The caller MUST `URL.revokeObjectURL` it when done /
 *  when the id changes / on unmount to avoid leaking blobs. */
export async function fetchImageObjectUrl(id: string): Promise<string | null> {
  try {
    const res = await authedFetch(`/api/backgrounds/${id}/image`);
    if (!res.ok) return null;
    const blob = await res.blob();
    return URL.createObjectURL(blob);
  } catch {
    return null;
  }
}
